import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Wheat, Plus } from "lucide-react";
import { menuCategories, buildWhatsAppLink } from "../data/menu";
import { Flourish } from "./brand/Ornaments";
import { useCart } from "../lib/CartContext";

const formatPrice = (v) => (v == null ? "—" : `R$ ${v}`);

const Menu = () => {
  const [active, setActive] = useState(menuCategories[0].id);
  const cat = menuCategories.find((c) => c.id === active);
  const { addItem, setIsCartOpen } = useCart();

  return (
    <section
      id="cardapio"
      className="relative py-24 sm:py-32 bg-[var(--sn-bg-deep)] overflow-hidden"
      data-testid="menu-section"
    >
      {/* Soft texture */}
      <div className="absolute inset-0 sn-grain pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="text-center mb-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex items-center justify-center gap-3 mb-6"
          >
            <span className="h-px w-10 bg-[var(--sn-gold)]" />
            <span className="text-[var(--sn-gold)] tracking-[0.3em] text-xs uppercase">
              Nosso Cardápio
            </span>
            <span className="h-px w-10 bg-[var(--sn-gold)]" />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--sn-cream)] mb-5"
            data-testid="menu-headline"
          >
            Sabores de família,{" "}
            <span className="italic gold-text">no ponto certo.</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="text-[var(--sn-cream-soft)]/80 max-w-2xl mx-auto"
          >
            Selecione suas massas preferidas, adicione ao carrinho e finalize o pedido de forma prática pelo WhatsApp.
          </motion.p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-12">
          {menuCategories.map((c) => (
            <button
              key={c.id}
              onClick={() => setActive(c.id)}
              className={`px-5 sm:px-7 py-3 text-sm tracking-wide rounded-sm transition-all border ${
                active === c.id
                  ? "bg-[var(--sn-gold)] text-[var(--sn-bg-deep)] border-[var(--sn-gold)]"
                  : "border-[var(--sn-border)] text-[var(--sn-cream-soft)]/85 hover:border-[var(--sn-gold)] hover:text-[var(--sn-gold)]"
              }`}
              data-testid={`menu-tab-${c.id}`}
            >
              {c.name}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={cat.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5 }}
            className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-14"
            data-testid={`menu-content-${cat.id}`}
          >
            {/* Category visual */}
            <div className="relative">
              <div className="relative overflow-hidden sn-frame">
                <img
                  src={cat.image}
                  alt={cat.name}
                  className="w-full h-[440px] lg:h-[560px] object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--sn-bg-deep)]/95 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="text-[var(--sn-gold)] text-xs tracking-[0.3em] uppercase mb-2">
                    Especialidade
                  </p>
                  <h3 className="font-display text-3xl sm:text-4xl text-[var(--sn-cream)] mb-2">
                    {cat.name}
                  </h3>
                  <p className="text-[var(--sn-cream-soft)]/80 text-sm max-w-md">
                    {cat.description}
                  </p>
                </div>
              </div>
              <p className="mt-5 font-serif-alt italic text-[var(--sn-gold)] text-lg">
                — {cat.tagline}
              </p>
            </div>

            {/* Items */}
            <div className="space-y-3">
              <div className="hidden sm:flex justify-between text-[10px] sm:text-xs uppercase tracking-[0.28em] text-[var(--sn-gold)]/80 border-b border-[var(--sn-border)] pb-3 mb-2">
                <span>Sabor</span>
                <span className="flex gap-14 mr-10">
                  <span>1 kg</span>
                  <span>500 g</span>
                </span>
              </div>

              {cat.items.map((it, idx) => (
                <motion.div
                  key={it.name}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: idx * 0.06 }}
                  className="group relative bg-[var(--sn-bg-surface)]/40 hover:bg-[var(--sn-bg-surface)]/80 border border-[var(--sn-border)] hover:border-[var(--sn-gold)] px-5 sm:px-6 py-5 transition-all duration-300"
                  data-testid={`menu-item-${cat.id}-${idx}`}
                >
                  <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <Wheat className="w-4 h-4 mt-1 text-[var(--sn-gold)] shrink-0" />
                      <div>
                        <h4 className="font-display text-xl text-[var(--sn-cream)] group-hover:text-[var(--sn-gold)] transition-colors">
                          {it.name}
                        </h4>
                        <p className="text-[var(--sn-cream-soft)]/70 text-sm mt-1 leading-relaxed">
                          {it.desc}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 text-left sm:items-center shrink-0 w-full sm:w-auto">
                      {it.price1kg != null && (
                        <div className="flex items-center justify-between sm:justify-start gap-3 bg-[var(--sn-bg-deep)]/40 p-2 sm:p-0 rounded-md sm:bg-transparent">
                          <div>
                            <p className="text-[10px] uppercase tracking-widest text-[var(--sn-gold)]/70 sm:hidden">
                              1 kg
                            </p>
                            <p className="font-display text-lg text-[var(--sn-gold)]">
                              {formatPrice(it.price1kg)}
                            </p>
                          </div>
                          <button
                            onClick={() => addItem({ id: `${cat.id}-${it.name}`, name: `${cat.name} ${it.name}`, size: "1kg", price: it.price1kg, quantity: 1 })}
                            className="w-8 h-8 flex items-center justify-center rounded-full border border-[var(--sn-gold)]/30 text-[var(--sn-gold)] hover:bg-[var(--sn-gold)] hover:text-[var(--sn-bg-deep)] transition-colors"
                            aria-label="Adicionar 1kg"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      
                      {it.price500g != null && (
                        <div className="flex items-center justify-between sm:justify-start gap-3 bg-[var(--sn-bg-deep)]/40 p-2 sm:p-0 rounded-md sm:bg-transparent">
                          <div>
                            <p className="text-[10px] uppercase tracking-widest text-[var(--sn-gold)]/70 sm:hidden">
                              500 g
                            </p>
                            <p className="font-display text-lg text-[var(--sn-cream-soft)]/85">
                              {formatPrice(it.price500g)}
                            </p>
                          </div>
                          <button
                            onClick={() => addItem({ id: `${cat.id}-${it.name}`, name: `${cat.name} ${it.name}`, size: "500g", price: it.price500g, quantity: 1 })}
                            className="w-8 h-8 flex items-center justify-center rounded-full border border-[var(--sn-border)] text-[var(--sn-cream-soft)] hover:border-[var(--sn-gold)] hover:text-[var(--sn-gold)] hover:bg-[var(--sn-gold)]/10 transition-colors"
                            aria-label="Adicionar 500g"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}

              <div className="pt-6">
                <button
                  onClick={() => setIsCartOpen(true)}
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-[var(--sn-gold)] text-[var(--sn-bg-deep)] font-medium px-7 py-3.5 rounded-md transition-all hover:bg-[var(--sn-cream)]"
                >
                  Ver Carrinho
                </button>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="mt-24 flex justify-center text-[var(--sn-gold)]/70">
          <Flourish className="w-40 h-5" />
        </div>
      </div>
    </section>
  );
};

export default Menu;
